import { Directive, ElementRef, HostListener } from '@angular/core';

@Directive({
  selector: '[sgtNumbersOnly]',
})
export class NumbersOnlyDirective {
  // Teclas permitidas
  private readonly ALLOWED_SPECIAL_KEYS = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Meta',
    'Command',
  ];

  constructor(private elementRef: ElementRef) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isAllowed =
      this.ALLOWED_SPECIAL_KEYS.includes(event.key) ||
      this.allowedSpecialKeysOnWindowsAndLinux(event) ||
      this.allowedSpecialKeysOnMacOS(event) ||
      this.allowedNumbersOnly(event);

    if (!isAllowed) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';

    // Remove todos os caracteres que não são números
    const numbersOnly = pastedData.replace(/[^0-9]/g, '');

    if (numbersOnly) {
      const input = this.elementRef.nativeElement as HTMLInputElement;
      const currentValue = input.value;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;

      // Inserir apenas os números na posição do cursor
      const newValue =
        currentValue.substring(0, start) +
        numbersOnly +
        currentValue.substring(end);
      input.value = newValue;

      // Posicionar o cursor após o texto colado
      const newCursorPosition = start + numbersOnly.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);

      // Disparar evento de input para Angular detectar a mudança
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;

    // Filtrar apenas números
    const numbersOnly = currentValue.replace(/[^0-9]/g, '');

    if (currentValue !== numbersOnly) {
      const cursorPosition = input.selectionStart || 0;
      input.value = numbersOnly;

      // Ajustar posição do cursor
      const newPosition = Math.min(cursorPosition, numbersOnly.length);
      input.setSelectionRange(newPosition, newPosition);
    }
  }

  /** Permite apenas números (0-9)
   *
   * @param event - Evento do teclado
   * @returns Verdadeiro se a tecla pressionada for um número
   */
  allowedNumbersOnly(event: KeyboardEvent): boolean {
    return /^[0-9]$/.test(event.key);
  }

  /**
   * Permite combinações de teclas especiais no macOS: Command+A, Command+C, Command+V, Command+X, Command+Z
   *
   * @param event - Evento do teclado
   * @returns Verdadeiro se a tecla pressionada for uma combinação especial permitida
   */
  allowedSpecialKeysOnMacOS(event: KeyboardEvent): boolean {
    return (
      event.metaKey &&
      ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
    );
  }

  /**
   * Permite combinações de teclas especiais no Windows e Linux: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
   *
   * @param event - Evento do teclado
   * @returns Verdadeiro se a tecla pressionada for uma combinação especial permitida
   */
  allowedSpecialKeysOnWindowsAndLinux(event: KeyboardEvent): boolean {
    return (
      event.ctrlKey &&
      ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())
    );
  }
}
